import { useState, useEffect } from "react";
import Login from "./Login";
import Dashboard from "./Dashboard";
import DocDetailTab from "./DocDetailTab";
import FilterDetailTab from "./FilterDetailTab";
import CaptureScreen from "./CaptureScreen";
import FilteredScreen from "./FilteredScreen";
import ProductDetailPage from "./ProductDetailPage";
import NewsFlashDetailPage from "./NewsFlashDetailPage";
import { loadSession, clearSession, saveSession, C } from "./utils";

const API = "https://cip.medsphere.net/api";

// ── Mobile update modal ───────────────────────────────────────────────────────
function MobileUpdateModal({ user, onDone, onSkip }) {
  const [mobile,  setMobile]  = useState("");
  const [otp,     setOtp]     = useState("");
  const [step,    setStep]    = useState("mobile"); // mobile | otp
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState("");
  const [sent,    setSent]    = useState(false);

  const requestOtp = async () => {
    if(!mobile.trim()) return setErr("Please enter your mobile number.");
    setLoading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("userid", user.USERID);
      fd.append("mobile", mobile.trim());
      const r   = await fetch(`${API}/request_mobile_otp`,
        {method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res = await r.json();
      if(res.STATUS==="1"||res.STATUS===1){ setStep("otp"); setSent(true); }
      else setErr(res.MESSAGE||"Failed to send OTP. Please try again.");
    } catch { setErr("Network error. Please try again."); }
    setLoading(false);
  };

  const verifyOtp = async () => {
    if(!otp.trim()) return setErr("Please enter the OTP.");
    setLoading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("userid", user.USERID);
      fd.append("mobile", mobile.trim());
      fd.append("otp",    otp.trim());
      const r   = await fetch(`${API}/update_mobile`,
        {method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res = await r.json();
      if(res.STATUS==="1"||res.STATUS===1){ onDone(mobile.trim()); }
      else setErr(res.MESSAGE||"Invalid OTP. Please try again.");
    } catch { setErr("Network error. Please try again."); }
    setLoading(false);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",
      zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"#fff",borderRadius:16,padding:"32px 28px",
        width:"min(420px,92vw)",boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>

        {/* Header */}
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{width:52,height:52,borderRadius:"50%",
            background:`${C.primary}15`,margin:"0 auto 12px",
            display:"flex",alignItems:"center",justifyContent:"center"}}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
              stroke={C.primary} strokeWidth="2" strokeLinecap="round">
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
            </svg>
          </div>
          <div style={{fontSize:18,fontWeight:700,color:C.text,marginBottom:4}}>
            Add Mobile Number
          </div>
          <div style={{fontSize:13,color:C.muted,lineHeight:1.5}}>
            {step==="mobile"
              ? "Your account doesn't have a mobile number. Add one to receive SMS notifications."
              : `OTP sent to ${mobile}. Enter it below to verify.`}
          </div>
        </div>

        {/* Input */}
        {step==="mobile" ? (
          <input value={mobile} onChange={e=>setMobile(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&requestOtp()}
            placeholder="+1 (555) 000-0000"
            style={{width:"100%",boxSizing:"border-box",padding:"10px 14px",
              borderRadius:8,border:`1.5px solid ${err?C.error:C.border}`,
              fontSize:15,outline:"none",fontFamily:"inherit",
              marginBottom:8}}/>
        ) : (
          <input value={otp} onChange={e=>setOtp(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&verifyOtp()}
            placeholder="Enter OTP"
            maxLength={8}
            style={{width:"100%",boxSizing:"border-box",padding:"10px 14px",
              borderRadius:8,border:`1.5px solid ${err?C.error:C.border}`,
              fontSize:20,letterSpacing:6,textAlign:"center",outline:"none",
              fontFamily:"inherit",marginBottom:8}}/>
        )}

        {err&&<div style={{fontSize:12,color:C.error,marginBottom:8}}>{err}</div>}

        {/* Actions */}
        <button
          onClick={step==="mobile"?requestOtp:verifyOtp}
          disabled={loading}
          style={{width:"100%",padding:"11px",borderRadius:8,border:"none",
            background:C.primary,color:"#fff",fontSize:14,fontWeight:600,
            cursor:loading?"not-allowed":"pointer",opacity:loading?0.7:1,
            marginBottom:8}}>
          {loading?"Please wait…":step==="mobile"?"Send OTP":"Verify & Save"}
        </button>

        {step==="otp"&&(
          <button onClick={()=>{setStep("mobile");setOtp("");setErr("");}}
            style={{width:"100%",padding:"9px",borderRadius:8,
              border:`1px solid ${C.border}`,background:"none",
              color:C.muted,fontSize:13,cursor:"pointer",marginBottom:8}}>
            ← Change Number
          </button>
        )}

        <button onClick={onSkip}
          style={{width:"100%",padding:"9px",borderRadius:8,
            border:"none",background:"none",
            color:C.hint,fontSize:12,cursor:"pointer"}}>
          Skip for now
        </button>
      </div>
    </div>
  );
}

// ── Terms & Privacy Policy modal ──────────────────────────────────────────────
function TermsModal({ user, onAccept, onDecline }) {
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState("");

  const respond = async (accept) => {
    if(!accept) { onDecline(); return; } // decline — no API call, just go back
    setLoading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("userid", user.USERID);
      fd.append("accept", "1");
      await fetch(`${API}/terms`,
        {method:"POST", headers:{"Auth-Code":user.AUTH_CODE}, body:fd});
      onAccept();
    } catch { setErr("Network error. Please try again."); }
    setLoading(false);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",
      zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
      <div style={{background:"#fff",borderRadius:16,width:"100%",maxWidth:640,
        maxHeight:"88vh",display:"flex",flexDirection:"column",
        boxShadow:"0 24px 64px rgba(0,0,0,0.2)",overflow:"hidden"}}>

        <div style={{padding:"18px 24px",borderBottom:"1px solid #E5E7EB",flexShrink:0}}>
          <div style={{fontSize:16,fontWeight:700,color:C.text}}>Terms & Privacy Policy</div>
          <div style={{fontSize:12,color:C.muted,marginTop:3}}>Please review and accept to continue</div>
        </div>

        <div style={{flex:1,overflow:"hidden"}}>
          <iframe src="https://pharmavision.com/privacy_policy.html"
            title="Privacy Policy"
            style={{width:"100%",height:"100%",border:"none"}}/>
        </div>

        {err&&<div style={{padding:"8px 24px",fontSize:12,color:"#B91C1C",
          background:"#FEF2F2",borderTop:"1px solid #FECACA"}}>{err}</div>}

        <div style={{padding:"14px 24px",borderTop:"1px solid #E5E7EB",
          display:"flex",alignItems:"center",gap:10,background:"#F9FAFB",flexShrink:0}}>
          <div style={{flex:1,fontSize:11,color:C.muted,lineHeight:1.4}}>
            By clicking Accept you agree to our Terms &amp; Privacy Policy
          </div>
          <button onClick={()=>respond(false)} disabled={loading}
            style={{padding:"9px 20px",borderRadius:8,border:`1px solid ${C.border}`,
              background:"#fff",color:C.muted,fontSize:13,fontWeight:600,
              cursor:loading?"not-allowed":"pointer",opacity:loading?0.6:1}}>
            Decline
          </button>
          <button onClick={()=>respond(true)} disabled={loading}
            style={{padding:"9px 22px",borderRadius:8,border:"none",
              background:"#16A34A",color:"#fff",fontSize:13,fontWeight:700,
              cursor:loading?"not-allowed":"pointer",opacity:loading?0.6:1}}>
            {loading?"Please wait…":"Accept"}
          </button>
        </div>
      </div>
    </div>
  );
}

const p          = new URLSearchParams(window.location.search);
const URL_DOC = p.get("doc");
const URL_DT  = Number(p.get("dt"));
const URL_PID = p.get("pid");
const URL_SUB = p.get("sub");
const URL_CAPTURE    = p.get("capture") === "1";
const URL_FILTER     = p.get("filter")  === "1";
const URL_FDETAIL    = p.get("fdetail") === "1";
const IS_DETAIL      = !!(URL_DOC && URL_DT);
const IS_NEWSFLASH   = IS_DETAIL && URL_DT === 99;
const IS_PROD_DETAIL = IS_DETAIL && URL_DT === 98;

export default function App() {
  const [stage,   setStage]   = useState("loading");
  const [session, setSession] = useState(null);
  const [pendingAuth, setPendingAuth] = useState(null);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [showTermsModal,  setShowTermsModal]  = useState(false);

  useEffect(() => {
    const cached = loadSession();
    if (!cached) { setStage("auth"); return; }

    if (URL_FILTER) {
      const subs = cached.user?.SUBPROJECTS || [];
      const matchSub = URL_SUB ? subs.find(s => String(s.SUBPROJECTID) === URL_SUB) : null;
      const activeSub = matchSub || cached.subproject || subs[0] || null;
      setSession({ ...cached, subproject: activeSub });
      setStage("filter");
    } else if (URL_CAPTURE) {
      const subs = cached.user?.SUBPROJECTS || [];
      const matchSub = URL_SUB ? subs.find(s => String(s.SUBPROJECTID) === URL_SUB) : null;
      const activeSub = matchSub || cached.subproject || subs[0] || null;
      setSession({ ...cached, subproject: activeSub });
      setStage("capture");
    } else if (URL_FDETAIL && IS_DETAIL) {
      const subs = cached.user?.SUBPROJECTS || [];
      const matchSub = URL_SUB ? subs.find(s => String(s.SUBPROJECTID) === URL_SUB) : null;
      const activeSub = matchSub || cached.subproject || subs[0] || null;
      setSession({ ...cached, subproject: activeSub });
      setStage("fdetail");
    } else if (IS_NEWSFLASH) {
      const subs = cached.user?.SUBPROJECTS || [];
      const matchSub = URL_SUB ? subs.find(s => String(s.SUBPROJECTID) === URL_SUB) : null;
      const activeSub = matchSub || cached.subproject || subs[0] || null;
      setSession({ ...cached, subproject: activeSub });
      setStage("newsflash");
    } else if (IS_DETAIL) {
      const subs     = cached.user?.SUBPROJECTS || [];
      const matchSub = URL_SUB ? subs.find(s => String(s.SUBPROJECTID) === URL_SUB) : null;
      const activeSub = matchSub || cached.subproject || subs[0] || null;
      setSession({ ...cached, subproject: activeSub });
      setStage(IS_PROD_DETAIL ? "product_detail" : "detail");
    } else {
      if (URL_SUB) {
        const subs = cached.user?.SUBPROJECTS || [];
        const matchSub = subs.find(s => String(s.SUBPROJECTID) === URL_SUB);
        if (matchSub) setSession({ ...cached, subproject: matchSub });
        else setSession(cached);
      } else {
        setSession(cached);
      }
      setStage("dashboard");
    }
  }, []);

  const proceedAfterAuth = (data) => {
    const subs   = data?.SUBPROJECTS || [];
    const defSub = URL_SUB
      ? (subs.find(s => String(s.SUBPROJECTID) === URL_SUB) || subs[0] || null)
      : (subs.find(s => s.SUBPROJECTID === data.MYDEFALULT) || subs[0] || null);
    const sess = { user: data, subproject: defSub };
    saveSession(sess);
    setSession(sess);
    setStage(URL_FILTER ? "filter" : URL_CAPTURE ? "capture" : URL_FDETAIL ? "fdetail" : IS_NEWSFLASH ? "newsflash" : IS_PROD_DETAIL ? "product_detail" : IS_DETAIL ? "detail" : "dashboard");
  };

  const handleAuthenticated = (data) => {
    if(data.TERMS === 0 || data.TERMS === "0") {
      // show terms modal first — before anything else
      setPendingAuth(data);
      setShowTermsModal(true);
    } else if(!data.PHONENUMBER || String(data.PHONENUMBER).trim()==="") {
      setPendingAuth(data);
      setShowMobileModal(true);
    } else {
      proceedAfterAuth(data);
    }
  };

  const handleLogout = () => {
    clearSession();
    setSession(null);
    // clear all sessionStorage nav keys
    Object.keys(sessionStorage).filter(k=>k.startsWith("pv_nav_")).forEach(k=>sessionStorage.removeItem(k));
    // clear URL params — take user to clean base path
    window.history.replaceState({}, "", window.location.pathname);
    setStage("auth");
  };

  if (stage === "loading") return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex",
      alignItems:"center", justifyContent:"center", color:C.muted, fontSize:"14px" }}>
      Loading...
    </div>
  );

  if (stage === "auth") return (
    <>
      <Login onAuthenticated={handleAuthenticated}/>
      {showTermsModal && pendingAuth && (
        <TermsModal
          user={pendingAuth}
          onAccept={()=>{
            setShowTermsModal(false);
            // after accepting terms check phone
            if(!pendingAuth.PHONENUMBER || String(pendingAuth.PHONENUMBER).trim()==="") {
              setShowMobileModal(true);
            } else {
              proceedAfterAuth(pendingAuth);
              setPendingAuth(null);
            }
          }}
          onDecline={()=>{
            setShowTermsModal(false);
            setPendingAuth(null);
          }}/>
      )}
      {showMobileModal && pendingAuth && (
        <MobileUpdateModal
          user={pendingAuth}
          onDone={(mobile)=>{
            setShowMobileModal(false);
            proceedAfterAuth({...pendingAuth, PHONENUMBER: mobile});
            setPendingAuth(null);
          }}
          onSkip={()=>{
            setShowMobileModal(false);
            proceedAfterAuth(pendingAuth);
            setPendingAuth(null);
          }}/>
      )}
    </>
  );

  if (stage === "filter" && session) return (
    <FilteredScreen session={session} onLogout={handleLogout} />
  );

  if (stage === "capture" && session) return (
    <CaptureScreen session={session} onLogout={handleLogout} />
  );

  if (stage === "fdetail" && session) return (
    <FilterDetailTab session={session} onLogout={handleLogout}
      docId={Number(URL_DOC)} doctypeId={URL_DT}/>
  );

  if (stage === "newsflash" && session) return (
    <NewsFlashDetailPage session={session} onLogout={handleLogout}
      docId={Number(URL_DOC)} pid={URL_PID}/>
  );

  if (stage === "detail" && session) return (
    <DocDetailTab
      session={session}
      onLogout={handleLogout}
      docId={Number(URL_DOC)}
      doctypeId={URL_DT}/>
  );

  if (stage === "product_detail" && session) return (
    <ProductDetailPage session={session} onLogout={handleLogout}/>
  );

  if (stage === "dashboard" && session) return (
    <Dashboard session={session} onLogout={handleLogout}/>
  );

  return <Login onAuthenticated={handleAuthenticated}/>;
}